import { useEffect, RefObject } from 'react';

/**
 * Makes every external link inside a container (admin-authored HTML rendered via
 * dangerouslySetInnerHTML) open in a new tab, keeping the current app tab.
 *
 * Detection uses the browser-RESOLVED anchor properties (a.protocol / a.hostname)
 * rather than the raw href string, so it also catches protocol-relative
 * (//example.com) and scheme-less (example.com) links. Same-host links and
 * in-page anchors (#…) are left untouched so they still navigate in-app.
 *
 * The container may not be mounted yet on the render where this effect first
 * fires (e.g. React 17 renders an intermediate frame where `detail` is set but
 * `loading` is still true, so the content div is not in the tree). To be robust
 * against that timing, we retry on the next animation frame until the ref is
 * attached, bounded by MAX_ATTEMPTS so a genuinely absent container doesn't spin.
 */
const MAX_ATTEMPTS = 30;

export default function useExternalLinksNewTab(
  ref: RefObject<HTMLElement>,
  dep: unknown,
): void {
  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    const apply = () => {
      if (cancelled) return;
      const root = ref.current;
      if (!root) {
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          requestAnimationFrame(apply);
        }
        return;
      }

      root.querySelectorAll('a').forEach((el) => {
        const anchor = el as HTMLAnchorElement;
        const isHttp =
          anchor.protocol === 'http:' || anchor.protocol === 'https:';
        const isExternal =
          isHttp && anchor.hostname !== window.location.hostname;
        if (isExternal) {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'noopener noreferrer');
        }
      });
    };

    apply();
    return () => {
      cancelled = true;
    };
  }, [ref, dep]);
}
